import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Shield, Users, X } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';

export default function RolesManager() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#6366F1' });

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data.roles || []);
    } catch (err) { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRoles(); }, []);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put('/roles/' + editing.id, form);
        toast.success('Role updated');
      } else {
        await api.post('/roles', form);
        toast.success('Role created');
      }
      setShowModal(false);
      setEditing(null);
      setForm({ name: '', description: '', color: '#6366F1' });
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm('Deactivate "' + role.name + '"? This will not delete existing assignments.')) return;
    try {
      await api.delete('/roles/' + role.id);
      toast.success('Role deactivated');
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name, description: role.description || '', color: role.color || '#6366F1' });
    setShowModal(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', color: '#6366F1' });
    setShowModal(true);
  };

  const COLORS = ['#DC2626','#EA580C','#D97706','#059669','#0891B2','#2563EB','#7C3AED','#DB2777','#6366F1','#64748B'];

  if (loading) return <div className="text-center py-12 text-gray-400">Loading roles...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <AdminPageHeader title="Roles" description="Define job roles for your firm. Assign them to team members." icon={Shield} iconColor="text-violet-600" iconBg="bg-violet-100">
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
          <Plus className="h-4 w-4" /> New Role
        </button>
      </AdminPageHeader>

      <div className="grid gap-3">
        {roles.map(role => (
          <div key={role.id} className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center justify-between group hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: role.color || '#6366F1' }}>
                {(role.name || '?')[0]}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{role.name}</span>
                  {role.is_system && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium">SYSTEM</span>
                  )}
                </div>
                {role.description && <p className="text-sm text-gray-500 mt-0.5">{role.description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Users className="h-3.5 w-3.5" />
                <span>{role.member_count || 0} assigned</span>
              </div>
              <button onClick={() => openEdit(role)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                <Pencil className="h-4 w-4" />
              </button>
              {!role.is_system && (
                <button onClick={() => handleDelete(role)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {roles.length === 0 && (
          <div className="text-center py-12 text-gray-400">No roles defined yet</div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold text-gray-900">{editing ? 'Edit Role' : 'New Role'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Lead Attorney, Paralegal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="What does this role do?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm({ ...form, color: c })}
                      className={'h-8 w-8 rounded-lg transition-all ' + (form.color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105')}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave}
                className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700">
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
